import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Price,
  PriceOwner,
  StorePriceSchema,
} from '../../models/catalog/price.model';
import { PriceService } from '../../services/catalog/price.service';
import { applyZodErrors } from '../../util/zod-form-errors';

@Component({
  selector: 'app-price-history',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [PriceService, MessageService, ConfirmationService],
  templateUrl: './price-history.component.html',
  styleUrl: './price-history.component.scss',
})
export class PriceHistoryComponent {
  #priceService = inject(PriceService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);

  /** Polymorphic owner (Product / Ticket / AdditionalProduct). Required. */
  owner = input.required<PriceOwner>();

  /** Parent-loaded price array. Required — single source of truth. */
  prices = input.required<Price[]>();

  priceCreated = output<Price>();
  priceDeleted = output<number>();

  /**
   * Latest non-future price row. Pure function of `prices()` + now().
   * Eliminates the "stale currentPriceId vs. fresh prices" bug class.
   */
  currentPrice = computed<Price | null>(() => {
    const now = Date.now();
    const past = this.prices()
      .filter((p) => Date.parse(p.valid_from) <= now)
      .sort(
        (a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from),
      );
    return past[0] ?? null;
  });

  /** Sorted by valid_from descending for display. */
  sortedPrices = computed<Price[]>(() =>
    [...this.prices()].sort(
      (a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from),
    ),
  );

  formVisible = signal(false);
  submitting = signal(false);

  form: FormGroup = this.#fb.group({
    valid_from: [new Date(), [Validators.required]],
    price: [null as number | null, [Validators.required, Validators.min(0)]],
    taxPercent: [null as number | null, [Validators.min(0), Validators.max(100)]],
  });

  /** Live net price computed from form values for U4 secondary display. */
  netPriceFormatted = signal<string | null>(null);

  showForm() {
    this.form.reset({ valid_from: new Date(), price: null, taxPercent: null });
    this.formVisible.set(true);
    this.netPriceFormatted.set(null);
    this.form.valueChanges.subscribe(() => this.#recomputeNet());
  }

  #recomputeNet() {
    const raw = this.form.getRawValue();
    const gross = typeof raw.price === 'number' ? raw.price : null;
    const taxPercent = typeof raw.taxPercent === 'number' ? raw.taxPercent : 0;
    if (gross === null) {
      this.netPriceFormatted.set(null);
      return;
    }
    const net = gross / (1 + taxPercent / 100);
    this.netPriceFormatted.set(
      net.toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
  }

  hideForm() {
    this.formVisible.set(false);
  }

  onSubmit() {
    if (this.submitting()) return;

    const raw = this.form.getRawValue();
    const taxDecimal =
      typeof raw.taxPercent === 'number'
        ? this.#toDecimalString(raw.taxPercent / 100)
        : null;
    const payload = {
      priceable_type: this.owner().type,
      priceable_id: this.owner().id,
      price: this.#toDecimalString(raw.price),
      tax: taxDecimal,
      valid_from: this.#toIsoDate(raw.valid_from),
    };

    const parsed = StorePriceSchema.safeParse(payload);
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    this.#priceService.createPrice(parsed.data).subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.formVisible.set(false);
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('catalog.prices.created'),
        });
        this.priceCreated.emit(response.data);
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant('catalog.prices.createError'),
        });
      },
    });
  }

  onDelete(price: Price, event: Event) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant('catalog.prices.deleteConfirmHeader'),
      message: this.#translate.instant('catalog.prices.deleteConfirmMessage'),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => this.#performDelete(price.id),
    });
  }

  #performDelete(id: number) {
    this.#priceService.deletePrice(id).subscribe({
      next: () => {
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('catalog.prices.deleted'),
        });
        this.priceDeleted.emit(id);
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant('catalog.prices.deleteError'),
        });
      },
    });
  }

  #toDecimalString(value: number | null): string {
    if (value === null || value === undefined) return '';
    return value.toFixed(2);
  }

  #toIsoDate(value: Date | string): string {
    if (typeof value === 'string') return value;
    // Server expects YYYY-MM-DD or full ISO; YYYY-MM-DD avoids timezone quirks.
    return value.toISOString().slice(0, 10);
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }

  isCurrent(price: Price): boolean {
    return this.currentPrice()?.id === price.id;
  }
}
